import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StyledMasonryContainer, StyledMasonryColumn, StyledMasonryItem } from './Masonry.styles';

interface MasonryProps {
  children: ReactElement[];
  columns?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  spacing?: number;
}

const Masonry: React.FC<MasonryProps> = ({ children, columns = { xs: 1, md: 2 }, spacing = 2 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(columns.xs || 1);
  const [items, setItems] = useState<ReactElement[][]>([]);

  const distributeItems = React.useCallback(() => {
    if (!children || children.length === 0) {
      setItems([]);
      return;
    }

    const newColumns: ReactElement[][] = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array.from({ length: columnCount }, () => 0);

    children.forEach((child) => {
      const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
      newColumns[minHeightIndex].push(child);
      columnHeights[minHeightIndex] += 1;
    });

    setItems(newColumns);
  }, [children, columnCount]);

  const getColumnCountCallback = React.useCallback(() => {
    if (!containerRef.current) return columns.xs || 1;
    
    const width = containerRef.current.offsetWidth;
    
    if (width >= 1536 && columns.xl) return columns.xl;
    if (width >= 1200 && columns.lg) return columns.lg;
    if (width >= 900 && columns.md) return columns.md;
    if (width >= 600 && columns.sm) return columns.sm;
    return columns.xs || 1;
  }, [columns]);

  useLayoutEffect(() => {
    const handleResize = () => {
      const newColumnCount = getColumnCountCallback();
      if (newColumnCount !== columnCount) {
        setColumnCount(newColumnCount);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [columnCount, getColumnCountCallback]);

  useEffect(() => {
    distributeItems();
  }, [distributeItems]);

  const spacingValue = spacing * 8; 

  return (
    <StyledMasonryContainer ref={containerRef} spacingValue={spacingValue}>
      {items.map((column, columnIndex) => (
        <StyledMasonryColumn key={columnIndex} spacingValue={spacingValue}>
          {column.map((item) => (
            <StyledMasonryItem key={item.key}>{item}</StyledMasonryItem>
          ))}
        </StyledMasonryColumn>
      ))}
    </StyledMasonryContainer>
  );
};

export default Masonry;